import { deriveAnchoredAge } from '../utils/levelCalculations';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Scatter, ZAxis, Rectangle
} from 'recharts';
import { FaUser, FaList, FaComments } from 'react-icons/fa';
import { DIMENSION_STYLES, UnifiedHeader, UnifiedLegend } from '../styles/DimensionStyles';
import { calculateExpectedPercentage, calculatePreviousYearPercentage } from '../utils/levelCalculations';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface SubDimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[]; // Para compatibilidad
  subDimensions?: SubDimension[];
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean;
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

interface DimensionSummaryProps {
  student: Student;
  dimensionId: number;
}

const COLORS = {
  previousYear: '#6B7280',
  achieved: '#4A90E2',
  expected: '#F5A623',
  headerBackground: '#1F2937', // Color de fondo para encabezados
  headerText: '#FFFFFF', // Color de texto para encabezados
};

// Datos de fallback para HabilidadesComunicacionales - TODAS LAS 26 HABILIDADES COMPLETAS
// Habilidades de Lenguaje Español (subdimensión 1)
const lenguajeEspanolSkills: Skill[] = [
  {
    "id": 1,
      "name": "Distingue al menos 2 sonidos, diferenciando si son iguales o diferentes.",
      "levels": [
      "Distingue al menos 2 sonidos, diferenciando si son iguales o diferentes.",
      "Distingue al menos 3 sonidos, diferenciando si son iguales o diferentes.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 2,
      "name": "Identifica cantidad y orden de sonidos escuchados. Al menos dos.",
      "levels": [
      "Identifica cantidad y orden de sonidos escuchados. Al menos dos.",
        "Identificae cantidad y orden de sonidos escuchados. Al menos tres.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 3,
      "name": "Reconoce la manipulación 2 de sonidos en una secuencia",
      "levels": [
        "Reconoce la manipulación 2 de sonidos en una secuencia",
        "Reconoce la manipulación de 3 sonidos en una secuencia",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 4,
      "name": "Reconoce cuando se agregan o quitan sonidos",
      "levels": [
        "Reconoce cuando se agregan o quitan sonidos",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 5,
      "name": "Separa palabras de 2 sílabas (CVCV o VCVC)",
      "levels": [
        "Separa palabras de 2 sílabas (CVCV o VCVC)",
      "Separa palabras de más de 2 sílabas.",
      "Cuenta las sílabas en una palabra.",
      "Cuenta sílabas en una palabra, separando incluso con combinaciones consonánticas.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 6,
      "name": "Reconoce de manera oral palabras que riman (zapato-gato) entre dos alternativas dadas",
      "levels": [
      "No Aplica",
        "Reconoce de manera oral palabras que riman (zapato-gato) entre dos alternativas dadas",
        "Reconoce de manera oral palabras que riman (zapato-gato) entre más de dos alternativas dadas",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 7,
      "name": "Reconoce al menos 3 sonidos de letras que forman su nombre",
      "levels": [
      "No Aplica",
        "Reconoce al menos 3 sonidos de letras que forman su nombre",
      "Reconoce al menos 3 sonidos de letras que forman una palabra de uso frecuente de más de dos sílabas.",
      "Identifica los sonidos que componen las palabras (conciencia fonológica), reconociendo, separando y combinando sus fonemas y sílabas.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 8,
      "name": "Reconoce el sonido inicial de su nombre",
      "levels": [
        "Reconoce el sonido inicial de su nombre",
      "Reconoce sonido inicial en una palabra.",
        "Reconoce sonido inicial y final en una palabra (manzana - campana)",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 9,
      "name": "Segmenta palabras (C/V/C ó V/C/V)",
      "levels": [
      "No Aplica",
        "No Aplica",
        "Segmenta palabras (C/V/C ó V/C/V)",
        "Segmenta palabras cortas con vocales o consonantes juntas (león, árbol, etc.)",
        "Segmenta palabras con combinaciones consonánticas.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 10,
      "name": "Nombra al menos 6 letras por su nombre",
      "levels": [
      "No Aplica",
        "Nombra al menos 6 letras por su nombre",
        "Nombra al menos 10 letras por su nombre",
        "Nombra al menos 20 letras por su nombre",
        "Nombra todas las letras por su nombre",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 11,
      "name": "Nombra al menos el sonido de 6 letras",
      "levels": [
      "No Aplica",
        "Nombra al menos el sonido de 6 letras",
        "Nombra el sonido de al menos 10 letras",
        "Nombra el sonido de al menos 20 letras",
        "Nombra el sonido de todas las letras",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 12,
      "name": "Une algunos sonidos para formar sílabas o palabras comunes",
      "levels": [
      "No Aplica",
        "No Aplica",
        "Une algunos sonidos para formar sílabas o palabras comunes",
      "Lee palabras aisladas y en contexto, aplicando su conocimiento de la correspondencia letra-sonido en diferentes combinaciones.",
      "Lee palabras aisladas y en contexto, aplicando su conocimiento de la correspondencia letra-sonido en diferentes combinaciones.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 13,
      "name": "Escribe algunas letras, intentando formar palabras.",
      "levels": [
        "No Aplica",
        "No Aplica",
      "Escribe algunas letras, intentando formar palabras.",
      "Escribe palabras simples, aunque presenta errores en algunas ocasiones.",
      "Escribe correctamente palabras de uso frecuente.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 14,
      "name": "Escribe correctamente palabras con combinaciones ce-ci, que-qui, ge-gi, gue-gui, güe-güi.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Escribe correctamente palabras con combinaciones ce-ci, que-qui, ge-gi, gue-gui, güe-güi.",
        "Escribe correctamente palabras con ge-gi y je-ji.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 15,
      "name": "Escribe correctamente palabras con combinaciones r-rr-nr.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
        "No Aplica",
        "Escribe correctamente palabras con combinaciones r-rr-nr.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 16,
      "name": "Escribe correctamente plurales de palabras terminadas en z.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
        "Escribe correctamente plurales de palabras terminadas en z.",
        "No Aplica",
        "No Aplica",
        "Escribe correctamente usando c-s-z"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 17,
      "name": "Escribe correctamente palabras con h de uso frecuente.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Escribe correctamente palabras con h de uso frecuente.",
        "Escribe correctamente reconociendo la diferencia entre ay, hay, ahí",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 18,
      "name": "Reconoce sílabas tónicas y átonas de palabras.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
        "Reconoce sílabas tónicas y átonas de palabras.",
        "Reconoce acentuación de palabras agudas, graves, esdrújulas y sobreesdrújulas.",
        "Escribe correctamente acentuación de palabras agudas, graves, esdrújulas y sobreesdrújulas.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 19,
      "name": "Escribe correctamente de acuerdo al acento diacrítico y diacrítico.",
      "levels": [
      "No Aplica",
      "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Escribe correctamente de acuerdo al acento diacrítico y diacrítico.",
        "Escribe correctamente la acentuación de pronombres interrogativos y exclamativos."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 20,
      "name": "Lee sobre 44 palabras en un minuto",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Lee sobre 44 palabras en un minuto",
        "Lee sobre 94 palabras en un minuto",
        "Lee sobre 112 palabras en un minuto",
        "Lee sobre 121 palabras en un minuto",
        "Lee sobre 131 palabras en un minuto",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 21,
      "name": "Lee con algo de expresión. Usa la voz para leer el texto como lenguaje natural, pero sólo en algunas partes.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Lee con algo de expresión. Usa la voz para leer el texto como lenguaje natural, pero sólo en algunas partes.",
        "Lee como si fuera lenguaje natural en casi todo el texto. Ocasionalmente cae en la lectura sin expresión.",
        "Lee como si fuera lenguaje natural utilizando una expresión adecuada.",
        "Lee con automatismo y precisión y con expresión adecuada.",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 22,
      "name": "Lee con un volumen generalmente apropiado para el texto.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Lee con un volumen generalmente apropiado para el texto.",
        "Lee con un volumen apropiado para el texto.",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 23,
      "name": "Lee presentando ocasionalmente pausas causadas por palabras poco frecuentes, partidas en falso, vacilaciones o repetición de palabras.",
      "levels": [
        "No Aplica",
        "No Aplica",
      "No Aplica",
      "Lee presentando ocasionalmente pausas causadas por palabras poco frecuentes, partidas en falso, vacilaciones o repetición de palabras.",
      "Lee generalmente de manera fluida, con algunos quiebres. Las dificultades con palabras y estructuras son resueltas rápidamente, generalmente por medio de la autocorrección.",
        "Lee de manera fluida, haciendo pausas naturales, apoyándose en la puntuación cuando corresponde.",
        "Lee de manera fluida, con adecuada entonación, apoyándose en la puntuación cuando corresponde.",
        "Lee de manera fluida con homogeneidad, decodificando de manera automática la mayoría de las palabras del texto.",
        "Lee de manera fluida con homogeneidad, decodificando de manera automática la mayoría de las palabras del texto."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 24,
      "name": "Presenta curiosidad por palabras que puede naturalmente inferir según el contexto.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
        "Presenta curiosidad por palabras que puede naturalmente inferir según el contexto.",
        "Descubre pistas en el texto para inferir el significado de una palabra.",
        "Es capaz de descubrir varias pistas en el texto para inferir el significado de una palabra.",
      "No Aplica",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 25,
      "name": "Reconoce que hay palabras que se pueden inferir según lo que se está observando y leyendo.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
        "Reconoce que hay palabras que se pueden inferir según lo que se está observando y leyendo.",
        "Reconoce que hay contextos 'ricos y pobres' para poder lograr inferencias y significado de las palabras.",
        "Utiliza palabras de un contexto, para inferir el significado de algunos conceptos.",
        "Utiliza naturalmente el inferir el significado por contexto"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 26,
      "name": "Reconoce y utiliza los prefijos des y sub.",
      "levels": [
      "No Aplica",
      "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Reconoce y utiliza los prefijos des y sub.",
        "Reconoce el significado y uso de palabras que tienen prefijos in, re, pre, y sufijos: mente.",
      "No Aplica"
    ],
      "selectedLevelIndex": null
    },
    {
      "id": 27,
      "name": "Reconoce y asocia palabras conocidas para ellos, de acuerdo a su familia léxica.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Reconoce y asocia palabras conocidas para ellos, de acuerdo a su familia léxica.",
        "No Aplica",
        "No Aplica",
        "Identifica y define palabras nuevas de acuerdo a su familia léxica.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 28,
      "name": "Reconoce las raíces de palabras conocidas, asociándolo al significativo.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Reconoce las raíces de palabras conocidas, asociándolo al significativo.",
        "Reconoce las raíces de las palabras nuevas, asociándolo al significado.",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 29,
      "name": "Nombra y realiza familias semánticas de manera grupal y en conversaciones.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "Nombra y realiza familias semánticas de manera grupal y en conversaciones.",
        "Reconoce y relaciona familias semánticas con palabras comunes.",
        "Reconoce y relaciona familias semánticas con palabras nuevas y palabras comunes.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 30,
      "name": "Determina significado de palabras desconocidas usando un diccionario infantil o ilustrado.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Determina significado de palabras desconocidas usando un diccionario infantil o ilustrado.",
        "Aplica estrategias para determinar significado de palabras, utilizando un diccionario, usando el orden alfabético.",
        "Aplica estrategias para determinar significado de palabras conocidas o nuevas, utilizando un diccionario u otro tipo de libro como enciclopedia.",
        "Aplica estrategias para determinar significado de palabras nuevas, utilizando un diccionario u otro tipo de libro como enciclopedia."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 31,
      "name": "Incorporar algunas palabras trabajadas a nivel oral.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "Incorporar algunas palabras trabajadas a nivel oral.",
        "Incorporar nuevas palabras de vocabulario.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 32,
      "name": "Comprende y utiliza los artículos.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Comprende y utiliza los artículos.",
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 33,
      "name": "Conoce algunas palabras que son sustantivos, diferenciando propios (como nombres) y comunes, utilizando palabras de uso frecuente.",
      "levels": [
        "No Aplica",
        "No Aplica",
        "No Aplica",
        "Conoce algunas palabras que son sustantivos, diferenciando propios (como nombres) y comunes, utilizando palabras de uso frecuente.",
        "Reconoce nuevas palabras que son sustantivos propios y comunes.",
        "No Aplica",
        "Utiliza sustantivos propios y comunes en escrituras y conversaciones, reconociendo su significado.",
        "Distingue matices entre sinónimos al leer, hablar y escribir para ampliar su comprensión y capacidad expresiva.",
        "Utiliza recursos que ofrece el lenguaje tales como: sinónimos, hipónimos, hiperónimos, comparaciones, etc. para expresarse eficientemente."
      ],
      "selectedLevelIndex": null
    }
];


// Habilidades de Escritura (subdimensión 2)
const escrituraSkills: Skill[] = [
  {
    "id": 34,
    "name": "Generar ideas de escritura",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Representa a través de dibujos o palabras ideas de escritura por medio de la discusión",
      "Genera ideas de escritura por medio de la discusión",
      "Genera ideas de escritura por medio de la discusión con los demás y por medio de material impreso.",
      "Genera ideas de escritura por medio de la discusión con los demás y por medio de material impreso, escribiéndolo.",
      "Genera ideas de escritura por medio de la discusión con los demás y por medio de material impreso, lee o genera una lista de ideas para escribir.",
      "Genera ideas de escritura por medio de la discusión con los demás y por medio de material impreso, generando una lista de ideas para escribir."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 35,
    "name": "Realizar lecturas preparatorias e investigaciones",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Cuando se le solicita, dentro de un momento de las actividades, lleva a cabo lecturas preparatorias, entrevistas o investigaciones cuando corresponde.",
      "Lleva a cabo lecturas preparatorias, entrevistas o investigaciones cuando corresponde."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 36,
    "name": "Desarrollar ideas iniciales",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Desarrolla ideas importantes para comenzar su escritura.",
      "Desarrolla una idea principal para comenzar su escritura.",
      "Desarrolla una idea principal para escribir, con claridad.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 37,
    "name": "Seleccionar tema o tesis",
    "levels": [
      "No Aplica",
      "Elige algo (objeto, momento o tema) que le gustaría comunicar",
      "No Aplica",
      "Elige un tema para su escritura",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Establece una afirmación de una tesis para un escrito informativo o plan para un escrito narrativo."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 38,
    "name": "Determinar propósito y audiencia",
    "levels": [
      "No Aplica",
      "Comienza a proponer el propósito de la escritura",
      "Propone dentro de conversaciones un posible propósito de la escritura.",
      "Determina un posible propósito y el público oyente",
      "Desarrolla el posible propósito y el público oyente",
      "Desarrolla a través de ideas un propósito y audiencia para la escritura.",
      "Desarrolla a través de ideas importantes un propósito y audiencia para la escritura.",
      "Desarrolla un propósito y audiencia para la escritura.",
      "Determina propósito y audiencia."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 39,
    "name": "Planificar la organización de ideas",
    "levels": [
      "No Aplica",
      "No Aplica",
      "Organiza y agrupa ideas relacionadas",
      "Usa estrategias organizacionales para planear escrituras",
      "No Aplica",
      "Usa estrategias organizativas de manera personal o grupal, como lluvia de ideas, listas, arañas o diagramas de Venn para planificar la escritura.",
      "Usa estrategias organizativas de manera personal como lluvia de ideas, listas, arañas o diagramas de Venn para planificar la escritura.",
      "Usa personalmente estrategias organizativas, pidiendo ayuda si lo necesita, para planificar la escritura como borradores de resúmenes, diagramas, mapas, arañas y diagrama de Venn.",
      "Usa personalmente estrategias organizativas para planificar la escritura como borradores de resúmenes, diagramas, mapas, arañas y diagrama de Venn."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 40,
    "name": "Estructurar el texto",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Estructura la escritura con un simple comienzo, desarrollo y sentido claro del final.",
      "No Aplica",
      "Estructura la escritura con un comienzo, desarrollo y un argumento determinado, seguido de una afirmación final, o un resumen de ideas y detalles importantes.",
      "Estructura la escritura con un comienzo o introducción, desarrollo y resolución de un argumento, seguido de una afirmación final, o un resumen de ideas y detalles importantes."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 41,
    "name": "Utilizar estructuras oracionales variadas",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa un surtido de oraciones simples, compuestas y complejas.",
      "Varía entre estructuras de oraciones simples, compuestas y complejas.",
      "No Aplica",
      "Varía entre estructuras de oraciones simples, compuestas y complejas; eligiendo según lo que quiere expresar."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 42,
    "name": "Crear párrafos con ideas de apoyo",
    "levels": [
      "No Aplica",
      "Produce historias, pensamientos u opiniones, usando dibujos y/o simulando letras o palabras.",
      "Escribe y/o ilustra una historia, pensamientos u opiniones, utilizando algunas palabras o frases.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Crea párrafos con oraciones de un tema, utilizando las oraciones de apoyo, unidas por palabras o frases de transición.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 43,
    "name": "Mantener coherencia en párrafos",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Agrupa en párrafos las ideas relacionadas incluyendo oraciones de un tema que siguen la forma de un párrafo adecuado, manteniendo un foco consistente durante todo el párrafo."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 44,
    "name": "Releer y revisar borradores",
    "levels": [
      "No Aplica",
      "Repite el mensaje representado a través de su \"escritura\".",
      "\"Relee\" su propia escritura",
      "Relee su propia escritura nombrando algunas palabras utilizadas.",
      "Relee su propia escritura tal como está escrita.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 45,
    "name": "Adaptar estilo y lenguaje",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa un lenguaje para la escritura que es distinto del lenguaje oral, imita el estilo de escritura de los libros en forma apropiada.",
      "No Aplica",
      "Varía el lenguaje y el estilo según la audiencia y el propósito.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 46,
    "name": "Usar recursos de referencia",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa recursos y materiales de referencia, incluyendo diccionarios, para seleccionar un vocabulario más efectivo.",
      "Usa recursos y materiales de referencia para seleccionar el vocabulario más claro, como los diccionarios.",
      "Usa fuentes y materiales de referencia para seleccionar un vocabulario más efectivo (por ejemplo, diccionarios o libros científicos)"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 47,
    "name": "Evaluar claridad de la escritura",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Relee y evalúa la escritura para lograr claridad usando métodos sugeridos como la silla del autor o el círculo del escritor.",
      "Relee y evalúa la escritura para lograr claridad usando una gama de métodos como la silla del autor o el círculo del escritor.",
      "Relee u analiza la claridad de la escritura.",
      "Relee u analiza la claridad de la escritura."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 48,
    "name": "Revisar contenido y coherencia",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Agrega palabras y detalles descriptivos y elimina información que es redundante.",
      "No Aplica",
      "Agrega y borra información y detalles para elaborar mejor una idea central y para cumplir mejor con el propósito.",
      "Agrega y borra información y detalles para elaborar una idea central y para lograr un propósito más efectivo."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 49,
    "name": "Reorganizar para mejorar claridad",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Reorganiza las palabras, oraciones y párrafos para aclarar el significado.",
      "No Aplica",
      "Reorganiza las palabras, oraciones y párrafos, y agrega palabras y frases de transición para aclarar el significado."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 50,
    "name": "Seleccionar vocabulario eficaz",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa recursos disponibles como los diccionarios, para seleccionar un vocabulario eficaz.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 51,
    "name": "Corregir ortografía y puntuación",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Corrige algunas partes del texto para mejorar las normas como en gramática, ortografía, puntuación y uso de mayúsculas; identificando y corrigiendo algunos fragmentos.",
      "Corrige casi siempre el texto, edita para mejorar las normas (ej: gramática, ortografía, puntuaciones, uso de mayúsculas) e identifica y corrige algunos fragmentos.",
      "Corrige el texto, aún con ayuda, edita para mejorar las normas (ej: gramática, ortografía, puntuaciones, uso de mayúsculas) e identifica y corrige algunos fragmentos.",
      "Corrige el texto, edita para mejorar las normas (ej: gramática, ortografía, puntuaciones, uso de mayúsculas) e identifica y corrige fragmentos."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 52,
    "name": "Usar herramientas de evaluación",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Aplica herramientas para formarse un juicio sobre la calidad de lo realizado, como listas de chequeo",
      "No Aplica",
      "Aplica herramientas para formarse un juicio sobre la calidad del escrito (por ejemplo rúbricas, listas de chequeo y retroalimentación)",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 53,
    "name": "Compartir y publicar escritos",
    "levels": [
      "No Aplica",
      "Comparte la escritura, ilustraciones e historias hacia otros",
      "Comparte \"leyendo\" su escritura, mostrando a través de una exhibición o compartiendo con otros.",
      "No Aplica",
      "Aplica herramientas para reescribir e ilustrar muestras para compartir.",
      "Reescribe e ilustra muestras para compartir.",
      "Prepara un escrito para su publicación que sigue un formato apropiado para el propósito, usando técnicas como diferentes materiales, imágenes u otros para destacar el producto final.",
      "Prepara un escrito para su publicación que sigue un formato apropiado para el propósito, usando técnicas como diferentes materiales, imágenes u otros para destacar el producto final, exhibiendo o compartiendo con otros.",
      "Prepara un escrito para su publicación que sigue un formato apropiado para el propósito, usando técnicas como diferentes materiales, recursos digitales, gráficos para destacar el producto final, exhibiendo o compartiendo con otros."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 54,
    "name": "Usar tecnología para escritura",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa tecnología disponible para crear un texto."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 55,
    "name": "Comunicar ideas gráficamente",
    "levels": [
      "No Aplica",
      "Produce historias, pensamientos u opiniones, usando dibujos y/o simulando letras o palabras.",
      "Escribe y/o ilustra una historia, pensamientos u opiniones, utilizando algunas palabras o frases.",
      "Escribe para comunicar a través del dibujo, utilizando algunas palabras u oraciones que quieran representar la escritura.",
      "Escribe historias que muestran un claro mensaje, detalles, un lenguaje vívido y que se mueve a través de una secuencia lógica de pasos y acontecimientos.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 56,
    "name": "Escribir narraciones detalladas",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe historias que muestran un mensaje claro, detalles descriptivos, un lenguaje vívido para desarrollar personajes, escenarios y argumentos; estableciendo un punto de vista, y moviéndose a través de una secuencia lógica de pasos y acontecimientos.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 57,
    "name": "Mantener punto de vista en narraciones",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe narraciones con un punto de vista consistente.",
      "Escribe narraciones que mantienen un objetivo claro y un punto de vista consistente."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 58,
    "name": "Usar detalles sensoriales y diálogos",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe historias o respuestas a novelas, cuentos y poemas mostrando que entendió el texto y respalda las opiniones haciendo referencia a partes específicas del texto.",
      "Escribe historias o respuestas a novelas, cuentos y poemas que incluyen una simple interpretación de un trabajo literario y respalda las opiniones haciendo referencia a partes específicas del texto original y al conocimiento previo.",
      "Escribe o responde a novelas, cuentos y poemas que organizan e incluyen una interpretación de varias ideas claras y respalda la interpretación a través de ejemplos y referencias a partes específicas del texto.",
      "Escribe o responde novelas, cuentos, poemas y obras de teatro que entregan una interpretación crítica o reflexión que apoyan los juicios con referencias específicas al texto."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 59,
    "name": "Escribir correspondencia formal e informal",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe cartas formales e informales como notas de agradecimiento o solicitudes que incluyen información y datos relevantes, saludo correcto, desarrollo, despedida y firma.",
      "Escribe cartas o \"mails\" formales e informales como notas de agradecimiento o solicitudes, que siguen el formato de las cartas, por ejemplo; fecha, saludo correcto, desarrollo, despedida y firma). Esto incluye información importante y muestra sentido de cierre.",
      "No Aplica",
      "Escribe cartas o \"mails\" que cumplen con el objetivo, hace solicitudes o da saludos, usando el formato adecuado para comunicarse."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 60,
    "name": "Escribir ensayos o reportes informativos con investigación",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe ensayos o reportes informativos que incluyen investigación, organizando la información con una introducción clara, desarrollo y conclusión, siguiendo la estructura expositiva cuando corresponde (por ejemplo, causa y efecto, comparación y contraste)",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 61,
    "name": "Desarrollar reportes informativos",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe reportes informativos que incluyen ideas principales y detalles significativos provenientes del texto.",
      "Escribe reportes informativos que incluyen hechos y ejemplos y presenta detalles importantes en un orden lógico.",
      "Escribe ensayos o reportes incluyendo hechos, detalles y ejemplos para ilustrar las ideas importantes.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 62,
    "name": "Escribir investigaciones detalladas",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe ensayos o reportes informativos que incluyen investigaciones, que presenta un entendimiento literal del tema, incluye hechos específicos, detalles y ejemplos provenientes de diferentes fuentes."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 63,
    "name": "Estructurar textos informativos",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe textos informativos creando una estructura apropiada organizada para propósito, audiencia y contexto."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 64,
    "name": "Escribir ensayos persuasivos",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Escribe ensayos persuasivos que establecen una clara posición e incluyen información relevante y detallada que apoya las ideas."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 65,
    "name": "Escribir textos informales",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Produce escritos informales como mensajes, bitácora, diario de vida o ideas con diferentes propósitos, utilizando imágenes y/o palabras y oraciones.",
      "Produce escritos informales como mensajes, bitácora, diario de vida o ideas con diferentes propósitos.",
      "Produce escritura informal con diferentes propósitos (Ej: diarios de vida, bitácoras, notas, poemas, etc)",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 66,
    "name": "Dominio de la dirección y formato de la escritura",
    "levels": [
      "No Aplica",
      "Finge escribir desde arriba hacia abajo, con filas horizontales como formato.",
      "Escribe de izquierda a derecha y de arriba hacia abajo, usando letras o palabras simples y dibujos.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 67,
    "name": "Deletrear palabras",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Deletrea y escribe correctamente palabras multisilábicas.",
      "Deletrea y escribe correctamente gran cantidad de palabras de uso frecuente, para su edad y etapa.",
      "Deletrea y escribe correctamente la mayoría de palabras de uso frecuente para su edad y etapa.",
      "Deletrea y escribe correctamente palabras que generalmente son escritas mal, aunque sean conocidas o de uso frecuente."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 68,
    "name": "Formación legible de escritura",
    "levels": [
      "No Aplica",
      "Escribe algunas letras de su nombre y otras \"palabras\" (símbolos) significativas para él.",
      "Escribe varias palabras, usando algunas de las letras que tiene, que representan su escritura.",
      "Escribe palabras y casi todas con sus letras, formando oraciones.",
      "Escribe legiblemente, dejando espacio entre letras, palabras y oraciones.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 69,
    "name": "Uso de comillas y apóstrofes",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa comillas en diálogos",
      "Usa en ciertas escrituras apóstrofes y comillas.",
      "No Aplica",
      "Usa correctamente apóstrofes y comillas."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 70,
    "name": "Puntuación avanzada",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa correctamente puntos aparte y comas en una serie.",
      "Usa correctamente comas, puntos aparte y puntos finales.",
      "Usa correctamente comas, puntos aparte, puntos finales, y comillas.",
      "Usa correctamente comas, puntos finales, punto y coma, dos puntos, guiones y paréntesis."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 71,
    "name": "Uso de mayúsculas",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Diferencia las letras entre mayúsculas y minúsculas.",
      "Utiliza letras mayúscula y minúsculas, tratando de usarlas según corresponde.",
      "Usa correctamente la mayúscula al comenzar las oraciones y en algunos nombres personales. ",
      "Usa correctamente la mayúscula al comenzar las oraciones y en los nombres personales ",
      "Usa correctamente la mayúscula al comenzar textos, oraciones y en los nombres y elementos que lo requieren.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 72,
    "name": "Uso de tiempos verbales",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa oraciones en pasado, presente y futuro.",
      "No Aplica",
      "No Aplica",
      "Usa verbos, incluyendo pretéritos, verbos transitivos e intransitivos y verbos que unen."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 73,
    "name": "Uso de pronombres",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa sujetos y pronombres",
      "Usa pronombres objetivos y normativos.",
      "No Aplica",
      "Usa pronombres normativos, objetivos, posesivos, indefinidos y relativos."
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 74,
    "name": "Uso de conjunciones y exclamaciones",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa conjunciones y exclamaciones.",
      "Usa conjunciones y exclamaciones.",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 75,
    "name": "Uso de adverbios",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa adverbios.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 76,
    "name": "Concordancia sujeto-verbo",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Usa sujetos y verbos que concuerdan.",
      "No Aplica",
      "Usa concordancia entre sujeto-verbo con sujetos colectivos, pronombres indefinidos, sujetos compuestos y frases preposicionales."
    ],
    "selectedLevelIndex": null
  }
];

// Combinar ambas subdimensiones para compatibilidad
const allCommunicationSkills: Skill[] = [...lenguajeEspanolSkills, ...escrituraSkills];

// Definición de la dimensión con datos hardcodeados y subdimensiones
export const habilidadesComunicacionalesDimension: Dimension = {
  id: 3,
  name: "Habilidades comunicacionales en el lenguaje español",
  skills: allCommunicationSkills, // Para compatibilidad
  subDimensions: [
    {
      id: 1,
      name: "Habilidades comunicacionales en español",
      skills: lenguajeEspanolSkills
    },
    {
      id: 2,
      name: "Escritura",
      skills: escrituraSkills
    }
  ]
};

const CustomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  return <Rectangle x={x} y={y} width={width} height={height} fill={fill} />;
};

const CustomVerticalRectangle = (props: any) => {
  const { cx, cy, fill } = props;
  return (
    <Rectangle
      x={cx - 1}
      y={cy - 25}
      width={4}
      height={40}
      fill={fill}
    />
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow">
        <p className="label font-bold">{`${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={`item-${entry.name}-${index}`} style={{ color: entry.fill }}>
            {`${entry.name}: ${entry.value.toFixed(2)}%`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const LEVEL_COLORS = {
  expected: 'bg-yellow-50 border-2 border-yellow-400',    // Color amarillo claro para nivel esperado
  previous: 'bg-gray-50 border-2 border-gray-400',        // Color gris claro para nivel del año anterior
  selected: 'bg-blue-500 text-white shadow-md',           // Color azul para nivel seleccionado
  default: 'bg-white hover:bg-blue-50 border border-gray-200', // Color por defecto
  notApplicable: 'bg-gray-50 text-gray-400 italic',      // Color para "No aplica" no seleccionado
  notApplicableSelected: 'bg-blue-500 text-white shadow-md italic', // Color para "No aplica" seleccionado
  previouslySelected: 'bg-blue-50 border border-blue-300', // Color para nivel previamente seleccionado
};

const HabilidadesComunicacionales: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number | null) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: 'student' | 'skill';
  handleViewModeChange: (mode: 'student' | 'skill') => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel,
}) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [showAllYears, setShowAllYears] = useState(true);
  
  // Estado para manejar las pestañas de subdimensiones
  const [activeSubDimension, setActiveSubDimension] = useState<number>(1); // 1 = Lenguaje Español, 2 = Escritura

  // Estado para rastrear niveles previamente seleccionados
  const [previouslySelectedLevels, setPreviouslySelectedLevels] = useState<{[key: string]: number | null}>();

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");

  // useEffect para manejar la selección por defecto
  useEffect(() => {
    if (viewMode === 'student' && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(new MouseEvent('click') as any, firstStudent.id as any);
      }
    } else if (viewMode === 'skill' && !selectedSkillId && getCurrentSubDimensionSkills().length > 0) {
      const firstSkill = getCurrentSubDimensionSkills()[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [viewMode, students, selectedStudentIds, handleStudentSelect, activeSubDimension]);

  // Función para obtener las habilidades de la subdimensión activa
  const getCurrentSubDimensionSkills = useCallback((): Skill[] => {
    const subDimension = habilidadesComunicacionalesDimension.subDimensions?.find(sub => sub.id === activeSubDimension);
    return subDimension ? subDimension.skills : lenguajeEspanolSkills;
  }, [activeSubDimension, lenguajeEspanolSkills]);

  // useEffect para cambiar a la primera habilidad cuando se cambia de subdimensión
  useEffect(() => {
    if (viewMode === 'skill') {
      const currentSkills = getCurrentSubDimensionSkills();
      if (currentSkills.length > 0) {
        // Verificar si la habilidad actual pertenece a la subdimensión activa
        const currentSkillExists = currentSkills.find(skill => skill.id === selectedSkillId);
        if (!currentSkillExists) {
          setSelectedSkillId(currentSkills[0].id);
        }
      } else {
        setSelectedSkillId(null);
      }
    }
  }, [activeSubDimension, viewMode, selectedSkillId, getCurrentSubDimensionSkills]);

  // useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};
      
      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === habilidadesComunicacionalesDimension.id && skillLevel.level !== null) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });
      
      // Solo actualizar si hay datos que inicializar
      if (Object.keys(initialLevels).length > 0) {
        setPreviouslySelectedLevels(initialLevels);
      }
    }
  }, [students]);
  
  // Función movida antes del useEffect
  
  // Función para obtener el color según el porcentaje de completado
  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'bg-indigo-500 text-white border-indigo-600 shadow-md transform scale-105';
    }
    
    if (percentage >= 75) {
      return 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100 hover:border-green-400';
    } else if (percentage >= 50) {
      return 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100 hover:border-blue-400';
    } else if (percentage >= 25) {
      return 'bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100 hover:border-yellow-400';
    } else {
      return 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100 hover:border-red-400';
    }
  };

  // Función para obtener el color del texto del porcentaje
  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) return 'text-white';
    if (percentage >= 75) return 'text-green-600 opacity-90';
    else if (percentage >= 50) return 'text-blue-600 opacity-90';
    else if (percentage >= 25) return 'text-yellow-600 opacity-90';
    else return 'text-red-600 opacity-90';
  };

  const getSkillLevel = (student: Student, dimensionId: number, skillId: number): number | null => {
    const skillLevel = student.skillLevels.find(sl => sl.dimensionId === dimensionId && sl.skillId === skillId);
    return skillLevel?.level ?? null;
  };

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === habilidadesComunicacionalesDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  const handleLocalLevelChange = useCallback((studentId: number | string, skillId: number, levelIndex: number | null) => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();

    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (student) {
      const currentLevel = getSkillLevel(student, habilidadesComunicacionalesDimension.id, skillId);

      if (currentLevel !== null && !previouslySelectedLevels?.[`${studentId}-${skillId}`]) {
        setPreviouslySelectedLevels(prev => ({
          ...prev,
          [`${studentId}-${skillId}`]: currentLevel
        }));
      }

      // Lógica de toggle: si el nivel ya está seleccionado, deseleccionarlo
      if (currentLevel === levelIndex) {
        handleLevelChange(studentId, habilidadesComunicacionalesDimension.id, skillId, null);
        return;
      }
    }

    // Si levelIndex es -1, convertir a null (para compatibilidad con el botón de reiniciar)
    const actualLevelIndex = levelIndex === -1 ? null : levelIndex;
    handleLevelChange(studentId, habilidadesComunicacionalesDimension.id, skillId, actualLevelIndex);
  }, [handleLevelChange, students, previouslySelectedLevels]);

  // Memoizar estudiantes filtrados con búsqueda y orden
  const filteredStudents = useMemo(() => {
    let filtered = selectedCourse
      ? students.filter(student => student.course === selectedCourse)
      : students;

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        student.name.toLowerCase().includes(searchLower) ||
        student.course.toLowerCase().includes(searchLower)
      );
    }

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "lastName":
          // Extraer el primer apellido (primera palabra del nombre completo)
          const firstLastNameA = a.name.split(' ')[0] || '';
          const firstLastNameB = b.name.split(' ')[0] || '';
          return firstLastNameA.localeCompare(firstLastNameB);
        case "course":
          return a.course.localeCompare(b.course);
        default:
          return 0;
      }
    });

    return filtered;
  }, [students, selectedCourse, searchTerm, sortBy]);

  // Función auxiliar para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = (fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  };

  const renderStudentView = useMemo(() => {
    return () => selectedStudentIds.map(studentId => {
      const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
      const student = students.find((s) => normalizeId(s.id) === normalizeId(studentId));
      if (!student) return null;

    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

      const expectedLevel = getExpectedLevel(age);

      // Generar array completo de años (4-12)
      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      // Filtrar años según la configuración
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        // Mostrar edad ±2 años (mínimo 4, máximo 12)
        const studentAge = age;
        const minAge = Math.max(4, studentAge - 2);
        const maxAge = Math.min(12, studentAge + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
        const match = year.match(/\d+/);
        const yearNum = match ? parseInt(match[0]) : 4;
        const calculatedIndex = yearNum - 4;
        // Asegurar que nunca sea NaN y usar fallback con index
        return isNaN(calculatedIndex) ? index : calculatedIndex;
      });
      
      // Obtener habilidades de la subdimensión activa
      const currentSkills = getCurrentSubDimensionSkills();

  return (
      <div key={student.id} className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              {/* Indicador de progreso */}
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de habilidades:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                        const progress = calculateFormProgress(
                          habilidadesComunicacionalesDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        const progressPercent = progress.percentage;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";
                        
                        return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
              </div>
                    <span className="text-xs text-gray-500">
                      {(() => {
                        const progress = calculateFormProgress(
                          habilidadesComunicacionalesDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                      })()}
                    </span>
              </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                    
                    <button
                      onClick={() => {
                        if (window.confirm("¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante?")) {
                          currentSkills.forEach(skill => {
                            // Pasar un valor para level que será interpretado como null
                            handleLocalLevelChange(student.id as any, skill.id, -1);
                          });
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                    >
                      Reiniciar
                    </button>
          </div>
        </div>
              </div>
              
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                      <tr>
                        <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">Habilidad</th>
                      {visibleYears.map((ageGroup, index) => {
                        const actualIndex = visibleIndices[index]; // Obtener el índice real del nivel
                        return (
                          <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                            <div className="flex flex-col items-center">
                              <span className="mb-1">{ageGroup}</span>
                              <button
                                onClick={() => {
                      currentSkills.forEach(skill => {
                                    const currentLevel = getSkillLevel(student, habilidadesComunicacionalesDimension.id, skill.id);
                                    if (currentLevel === actualIndex) {
                                      handleLocalLevelChange(student.id as any, skill.id, null);
                                    } else {
                                      handleLocalLevelChange(student.id as any, skill.id, actualIndex);
                                    }
                                  });
                                }}
                                className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                              >
                                Seleccionar todo
                              </button>
                            </div>
                          </th>
                        );
                      })}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {currentSkills.map((skill: Skill, skillIndex: number) => {
                        const selectedLevel = getSkillLevel(student, habilidadesComunicacionalesDimension.id, skill.id);
                        const skillIsNotEvaluated = isSkillNotEvaluated(student, habilidadesComunicacionalesDimension.id, skill.id);
                      
                        const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                        return (
                        <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                            <td className="py-3 px-6 text-sm font-medium text-gray-900">
                            <div className="flex items-start">
                            <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                              {skillIndex + 1}
                            </span>
                              <div className="flex-1">
                              {skill.name}
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, habilidadesComunicacionalesDimension.id, skill.id, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            </td>
                          {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                            const level = skill.levels[levelIndex];
                            if (!level) return null; // Si no existe el nivel para este índice, no mostrar nada
                            
                            const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                            const isExpected = levelIndex === expectedLevel;
                            const isPrevious = levelIndex === previousYearLevel;
                            
                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                            
                            // 1. Fondo según selección o estado base
                            if (isNotApplicable) {
                              // Si es "No aplica", verificar si está seleccionado
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.selected;
                            } else if (previouslySelected) {
                              // Mostrar nivel previamente seleccionado
                              buttonClass += LEVEL_COLORS.previouslySelected;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                            let extraStyles: React.CSSProperties = {
                              boxSizing: 'border-box'
                            };

                            if (isPrevious) {
                              // Aplicar borde gris grueso para nivel año anterior
                              extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                              }
                            }

                            if (isExpected) {
                              // Si es esperado, aplicar borde amarillo
                              if (!isPrevious) {
                                extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                                if (selectedLevel !== levelIndex) {
                                  extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                                }
                              }
                            }
                            
                            return (
                              <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                                <button
                                  onClick={() => handleLocalLevelChange(student.id as any, skill.id, levelIndex)}
                                  className={buttonClass}
                                  style={extraStyles}
                                  title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                                >
                                  <span className="text-center leading-tight">{level}</span>
                                </button>
                              </td>
                            );
                          })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              
              {/* Controles de navegación rápida */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
              </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>
    );
    });
  }, [selectedStudentIds, students, getExpectedLevel, getPreviousYearLevel, getCurrentSubDimensionSkills, showAllYears, handleLocalLevelChange, previouslySelectedLevels]);

  const renderSkillView = useMemo(() => {
    return () => {
      if (!selectedSkillId) return null;
      
      const skill = getCurrentSubDimensionSkills().find(s => s.id === selectedSkillId);
      if (!skill) return null;

      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        const averageAge = filteredStudents.length > 0 ? 
          filteredStudents.reduce((sum, student) => {
            const a = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
            return sum + a;
          }, 0) / filteredStudents.length : 6;
        
        const minAge = Math.max(4, Math.floor(averageAge) - 2);
        const maxAge = Math.min(12, Math.floor(averageAge) + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
        const match = year.match(/\d+/);
        const yearNum = match ? parseInt(match[0]) : 4;
        const calculatedIndex = yearNum - 4;
        // Asegurar que nunca sea NaN y usar fallback con index
        return isNaN(calculatedIndex) ? index : calculatedIndex;
      });

      // Estado masivo de "No evaluada" para la habilidad seleccionada
      const allStudentsNotEvaluated =
        filteredStudents.length > 0 &&
        filteredStudents.every((student) =>
          isSkillNotEvaluated(student, habilidadesComunicacionalesDimension.id, skill.id)
        );

      const toggleNotEvaluatedForAll = () => {
        if (!handleNotEvaluatedChange) return;
        const nextState = !allStudentsNotEvaluated;
        filteredStudents.forEach((student) => {
          handleNotEvaluatedChange(
            student.id,
            habilidadesComunicacionalesDimension.id,
            selectedSkillId,
            nextState
          );
        });
      };

      return (
        <div className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de la habilidad:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        const totalStudents = filteredStudents.length;
                        const completedStudents = filteredStudents.filter(student => 
                          getSkillLevel(student, habilidadesComunicacionalesDimension.id, skill.id) !== null
                        ).length;
                        const progressPercent = totalStudents > 0 ? (completedStudents / totalStudents) * 100 : 0;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";

    return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-gray-500">
                      {filteredStudents.filter(student => 
                        getSkillLevel(student, habilidadesComunicacionalesDimension.id, skill.id) !== null
                      ).length} de {filteredStudents.length} estudiantes
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{skill.name}</span>
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                  </div>
                </div>
            </div>
          
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                    <tr>
                      <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                        <div className="flex flex-col items-start gap-1">
                          <span>Estudiante</span>
                          {handleNotEvaluatedChange && (
                            <button
                              onClick={toggleNotEvaluatedForAll}
                              className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                                allStudentsNotEvaluated
                                  ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                  : "bg-white text-indigo-900 border-white/50 hover:bg-indigo-50"
                              }`}
                              title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                            >
                              {allStudentsNotEvaluated
                                ? "✓ Habilitar evaluación"
                                : "🚫 No evaluar esta habilidad"}
                            </button>
                          )}
                        </div>
                      </th>
                      {visibleYears.map((ageGroup, index) => {
                        const actualIndex = visibleIndices[index];
                        return (
                          <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                            <div className="flex flex-col items-center">
                              <span className="mb-1">{ageGroup}</span>
                              <button
                                onClick={() => {
                                  filteredStudents.forEach(student => {
                                    const currentLevel = getSkillLevel(student, habilidadesComunicacionalesDimension.id, selectedSkillId);
                                    if (currentLevel === actualIndex) {
                                      handleLocalLevelChange(student.id as any, selectedSkillId, null);
                                    } else {
                                      handleLocalLevelChange(student.id as any, selectedSkillId, actualIndex);
                                    }
                                  });
                                }}
                                className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                              >
                                Seleccionar todo
                              </button>
                            </div>
                    </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStudents.map((student, studentIndex) => {
                      const selectedLevel = getSkillLevel(student, habilidadesComunicacionalesDimension.id, selectedSkillId);
                      const skillIsNotEvaluated = isSkillNotEvaluated(student, habilidadesComunicacionalesDimension.id, selectedSkillId);
                      
                    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
                  
                      return (
                        <tr key={student.id} className={`${studentIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                          <td className="py-3 px-6 text-sm font-medium text-gray-900">
                            <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                          <span className="text-indigo-800 font-bold text-xs">{student.name.split(' ')[0][0]}{student.name.split(' ')[1]?.[0]}</span>
                        </div>
                              <div className="ml-1 flex-1">
                                <div className="font-medium">{student.name}</div>
                                <div className="text-xs text-gray-500">{student.course}</div>
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, habilidadesComunicacionalesDimension.id, selectedSkillId, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                            const level = skill.levels[levelIndex];
                            if (!level) return null;
                            
                            const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                            
                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                            
                            const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                            const expectedLevel = getExpectedLevel(age);

                            const isExpected = levelIndex === expectedLevel;
                            const isPrevious = levelIndex === previousYearLevel;

                            // 1. Fondo según selección o estado base
                            if (isNotApplicable) {
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.selected;
                            } else if (previouslySelected) {
                              buttonClass += LEVEL_COLORS.previouslySelected;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                            let extraStyles: React.CSSProperties = {
                              boxSizing: 'border-box'
                            };

                            if (isPrevious) {
                              // Aplicar borde gris grueso para nivel año anterior
                              extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                              }
                            }

                            if (isExpected) {
                              // Si es esperado, aplicar borde amarillo
                              if (!isPrevious) {
                                extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                                if (selectedLevel !== levelIndex) {
                                  extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                                }
                              }
                            }
                        
                              return (
                          <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}`} className="py-2 px-2 text-center">
                            <button
                                  onClick={() => handleLocalLevelChange(student.id as any, selectedSkillId, levelIndex)}
                                    className={buttonClass}
                                    style={extraStyles}
                                  title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                            >
                                  <span className="text-center leading-tight">{level}</span>
                            </button>
                          </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>
              
              {/* Controles de navegación rápida */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>
    );
  };
  }, [selectedSkillId, getCurrentSubDimensionSkills, filteredStudents, showAllYears, handleLocalLevelChange, previouslySelectedLevels, getExpectedLevel, getPreviousYearLevel]);

  const renderSelectionButtons = () => {
    if (viewMode === 'skill') {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Habilidad:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar habilidad..." 
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'course' | 'lastName')}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Número</option>
                  <option value="course">% Completado</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {getCurrentSubDimensionSkills()
              .filter(skill => 
                searchTerm === '' || 
                skill.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((skill) => {
                const completed = filteredStudents.filter(student => 
                getSkillLevel(student, habilidadesComunicacionalesDimension.id, skill.id) !== null
              ).length;
                const percentage = filteredStudents.length > 0 ? (completed / filteredStudents.length) * 100 : 0;
              
              return (
                <button
                  key={skill.id}
                  onClick={() => setSelectedSkillId(skill.id)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, selectedSkillId === skill.id)
                }`}
              >
                    <div className="font-semibold" title={skill.name}>
                {skill.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                    {percentage.toFixed(1)}% completado
                  </div>
                </button>
              );
            })}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
                <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Selecciona una habilidad para ver detalles
                </span>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar estudiante..." 
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
                No se encontraron estudiantes con "{searchTerm}"
              </div>
            ) : (
              filteredStudents.map((student) => {
                // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                const progress = calculateFormProgress(
                  habilidadesComunicacionalesDimension.id,
                  student.course,
                  student.skillLevels
                );
                const percentage = progress.percentage;
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
                
                return (
                  <button
                    key={student.id}
                    onClick={(event) => handleStudentSelect(event, student.id as any)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, isSelected)
                    }`}
                  >
                    <div className="font-semibold" title={`${student.name} - ${student.course}`}>
                      {student.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                  </button>
                );
              })
            )}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
              <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Ctrl + clic para seleccionar múltiples estudiantes
              </span>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderDataInputView = useMemo(() => {
    return () => (
      <div>
        {viewMode === 'student' ? renderStudentView() : renderSkillView()}
      </div>
    );
  }, [viewMode, renderStudentView, renderSkillView]);

      if (getCurrentSubDimensionSkills().length === 0) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Cargando habilidades comunicacionales...</p>
      </div>
    );
  }

  if (getCurrentSubDimensionSkills().length === 0) {
    return (
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
        <p className="font-bold">Advertencia</p>
        <p>No se pudieron cargar las habilidades desde el archivo CSV. Contacte al administrador.</p>
      </div>
    );
  }

  return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName={habilidadesComunicacionalesDimension.name}
        icon={FaComments}
        skillsCount={getCurrentSubDimensionSkills().length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
      
      {/* Pestañas de Subdimensiones */}
      <div className="mt-4 mb-4">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-lg">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => setActiveSubDimension(1)}
              className={`flex items-center justify-center py-3 px-4 rounded-md text-sm transition-all duration-200 ${
                activeSubDimension === 1
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaComments className="mr-2 text-sm" />
              <span className="font-medium">Habilidades comunicacionales en español</span>
              <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                {lenguajeEspanolSkills.length}
              </span>
            </button>
            
            <button
              onClick={() => setActiveSubDimension(2)}
              className={`flex items-center justify-center py-3 px-4 rounded-md text-sm transition-all duration-200 ${
                activeSubDimension === 2
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaUser className="mr-2 text-sm" />
              <span className="font-medium">Escritura</span>
              <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                {escrituraSkills.length}
              </span>
            </button>
          </div>
        </div>
      </div>
        
      {/* Botones de modo de vista */}
      <div className="mt-3">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => handleViewModeChange('student')}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'student' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaUser className="mr-1.5 text-sm" />
              <span className="font-medium">Por Estudiante</span>
            </button>
            
            <button
              onClick={() => handleViewModeChange('skill')}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'skill' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaList className="mr-1.5 text-sm" />
              <span className="font-medium">Por Habilidad</span>
            </button>
          </div>
        </div>
      </div>
      
      {renderSelectionButtons()}
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        {renderDataInputView()}
        
        {/* Leyenda de colores para los niveles de las tablas */}
        {(viewMode === 'student' && selectedStudentIds.length > 0) || (viewMode === 'skill' && selectedSkillId !== null) ? (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🎯</span>
              Leyenda de colores para niveles de desarrollo (hasta 12 años):
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2 shadow-sm border"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel seleccionado</span> - Nivel evaluado para el estudiante
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel esperado</span> - Nivel correspondiente a la edad
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 border-2 border-gray-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel año anterior</span> - Nivel del año previo
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-50 border border-blue-300 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Previamente seleccionado</span> - Nivel anterior guardado
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border border-gray-200 rounded mr-2 hover:bg-blue-50"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Sin seleccionar</span> - Nivel disponible para evaluar
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 rounded mr-2 border flex items-center justify-center">
                  <span className="text-xs text-gray-400 italic">N/A</span>
                </div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">No aplica</span> - No corresponde para esta edad
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-xs text-gray-500 italic">
                💡 <strong>Instrucciones:</strong> Haz clic en los niveles para seleccionar el desarrollo actual del estudiante. 
                Los colores te ayudan a identificar el progreso esperado según la edad (4-12 años).
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// Funciones auxiliares para cálculos de porcentajes
// Usar funciones centralizadas

const calculateExpectedLevel = (age: number) => {
  return calculateExpectedPercentage(age);
};

const calculateLastYearPercentage = (age: number) => {
  return calculatePreviousYearPercentage(age);
};

export default HabilidadesComunicacionales;